import { FlashcardDecksService, FlashcardsService } from "@/client"
import { AppEmptyState } from "@/components/Common/AppEmptyState"
import { SessionCompleteSummary } from "@/components/Flashcards/SessionCompleteSummary"
import { StudySession } from "@/components/Flashcards/StudySession"
import type { StudySessionCard } from "@/components/Flashcards/StudySession"
import { PageShell, PageSkeleton } from "@/components/Layout"
import useAuth from "@/hooks/useAuth"
import {
  clearStudySession,
  loadStudySession,
  saveStudySession,
} from "@/hooks/useStudySessionStore"
import { useRouteBreadcrumbs } from "@/navigation/route-meta"
import { Button } from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useEffect, useState } from "react"

export const Route = createFileRoute("/_layout/study/$deckId")({
  component: StudyDeckPage,
})

function StudyDeckPage() {
  const { deckId } = Route.useParams()
  const navigate = useNavigate()
  const breadcrumbs = useRouteBreadcrumbs("study.index", [
    { label: "Deck Session" },
  ])
  const { user } = useAuth()
  const sessionSize = user?.study_session_size ?? 50

  const mode = "deck" as const
  const paramsKey = JSON.stringify({ deckId })

  // Fetch the full due list (uncapped) and filter to this deck client-side, so
  // the deck's due cards aren't starved by a global cap that other decks fill
  // first. The session cap is then applied to THIS deck's cards in
  // getStudyCards.
  const { data: dueData, isLoading: isDueLoading } = useQuery({
    queryKey: ["flashcards-due", "uncapped"],
    queryFn: () => FlashcardsService.getDueCards(),
    select: (d) => ({ ...d, data: d.data.filter((c) => c.deck_id === deckId) }),
  })

  const { data: deckCardsData, isLoading: isDeckCardsLoading } = useQuery({
    queryKey: ["flashcard-deck-cards", deckId],
    queryFn: () => FlashcardDecksService.listCards({ deckId }),
  })

  const [done, setDone] = useState(false)
  // `done` alone isn't enough: getStudyCards caps a session at sessionSize,
  // so a "finished" session can still leave due cards behind. StudySession's
  // review mutation invalidates the due-cards query on session completion
  // (before calling onComplete), so dueData is fresh by the time `done` is
  // set — checking it directly confirms the deck is actually fully cleared.
  const { data: nextDeck } = useQuery({
    queryKey: ["flashcard-deck-next-in-series", deckId],
    queryFn: () => FlashcardDecksService.getNextInSeries({ deckId }),
    enabled: done && (dueData?.data.length ?? 0) === 0,
  })
  const [xpEarned, setXpEarned] = useState(0)
  const [sessionCards, setSessionCards] = useState<StudySessionCard[] | null>(
    null,
  )
  const [sessionIsRestudy, setSessionIsRestudy] = useState<boolean | null>(null)
  const [resumeIndex, setResumeIndex] = useState(0)
  const [resumeXp, setResumeXp] = useState(0)

  useEffect(() => {
    if (!isDueLoading && !isDeckCardsLoading && sessionCards === null) {
      const restored = loadStudySession()
      const matchesSnapshot =
        restored !== null &&
        restored.mode === mode &&
        JSON.stringify(restored.params) === paramsKey &&
        restored.cards.length > 0 &&
        restored.index < restored.cards.length

      if (matchesSnapshot && restored !== null) {
        setSessionCards(restored.cards)
        setResumeIndex(restored.index)
        setResumeXp(restored.xpEarned)
        setSessionIsRestudy(null)
      } else {
        const initialDueCards = dueData?.data ?? []
        const initialCards = getStudyCards(
          initialDueCards,
          deckCardsData?.data ?? [],
          sessionSize,
        )
        setSessionCards(initialCards)
        setSessionIsRestudy(
          initialDueCards.length === 0 && initialCards.length > 0,
        )
        saveStudySession({
          mode,
          params: { deckId },
          cards: initialCards,
          index: 0,
          xpEarned: 0,
          streak: 0,
          savedAt: Date.now(),
        })
      }
    }
  }, [
    deckCardsData?.data,
    dueData?.data,
    isDeckCardsLoading,
    isDueLoading,
    sessionCards,
    sessionSize,
    paramsKey,
    mode,
    deckId,
  ])

  const handleProgress = (index: number, xp: number) => {
    saveStudySession({
      mode,
      params: { deckId },
      cards: sessionCards ?? [],
      index,
      xpEarned: xp,
      streak: 0,
      savedAt: Date.now(),
    })
  }

  if (isDueLoading || isDeckCardsLoading) {
    return (
      <PageShell title="Deck Session" breadcrumbs={breadcrumbs}>
        <PageSkeleton variant="detail" />
      </PageShell>
    )
  }

  const dueCards = dueData?.data ?? []
  const cards =
    sessionCards ??
    getStudyCards(dueCards, deckCardsData?.data ?? [], sessionSize)
  const isRestudySession =
    sessionIsRestudy ?? (dueCards.length === 0 && cards.length > 0)

  if (done || cards.length === 0) {
    return (
      <PageShell title="Deck Session" breadcrumbs={breadcrumbs}>
        {done ? (
          <SessionCompleteSummary
            cardsCompleted={cards.length}
            xpEarned={xpEarned}
            onBack={() => navigate({ to: "/study" })}
            secondaryAction={
              nextDeck
                ? {
                    label: `Study ${nextDeck.name}`,
                    onClick: () =>
                      navigate({
                        to: "/study/$deckId",
                        params: { deckId: nextDeck.id },
                      }),
                  }
                : undefined
            }
          />
        ) : (
          <AppEmptyState
            title="Nothing due for this deck"
            description="You are caught up for now."
            action={
              <Button onClick={() => navigate({ to: "/study" })}>
                Back to Study Hub
              </Button>
            }
          />
        )}
      </PageShell>
    )
  }

  const modeLabel = isRestudySession ? "Deck run" : "Deck run"

  return (
    <PageShell
      title="Deck Session"
      breadcrumbs={breadcrumbs}
      py={{ base: 5, md: 8 }}
      contentGap={5}
    >
      <StudySession
        cards={cards}
        modeLabel={modeLabel}
        initialIndex={resumeIndex}
        initialXp={resumeXp}
        onProgress={handleProgress}
        onComplete={(xp) => {
          clearStudySession()
          setDone(true)
          setXpEarned(xp)
        }}
      />
    </PageShell>
  )
}

function getStudyCards(
  dueCards: StudySessionCard[],
  deckCards: StudySessionCard[],
  cap: number,
): StudySessionCard[] {
  if (dueCards.length > 0) {
    return dueCards.slice(0, cap)
  }
  return deckCards.slice(0, cap)
}

export default StudyDeckPage
